<script lang="ts">
  import { twMerge } from 'tailwind-merge';

  import type { ArticleInfoProps } from '$lib/types/components';
  import Button from './Button.svelte';
  import ProfileImage from './ProfileImage.svelte';
  import RevisionDate from './RevisionDate.svelte';

  type $$Props = ArticleInfoProps & {
    categoryClass?: string;
    metadataClass?: string;
    headingSize?: 'md' | 'lg';
    hideAuthorImageMobile?: boolean;
  };

  export let headingTag: $$Props['headingTag'] = 'h3';
  export let headingSize: $$Props['headingSize'] = 'md';
  export let scheme: $$Props['scheme'] = 'light';

  const {
    title,
    description,
    revisionDate,
    authorName,
    authorImage,
    authorImageAlt,
    hideAuthorImageMobile,
    category,
    categoryClass,
    metadataClass
  } = $$props as $$Props;

  let headingClasses = {
    md: 'md:text-base text-sm font-bold',
    lg: 'lg:text-3xl md:text-base text-sm font-semibold'
  };

  const authorImgAltText = authorImageAlt ?? `Author profile image for ${authorName}`;
</script>

<div class={twMerge('flex flex-col', scheme === 'dark' && ' text-white', $$restProps.class)}>
  {#if category}
    <Button
      href={category.href}
      variant="link"
      class={twMerge(
        '_text-caps mb-2 !text-xxs leading-none text-gray hover:text-blue md:mb-3',
        scheme == 'dark' && 'text-white ',
        categoryClass
      )}
    >
      {category.text}
    </Button>
  {/if}

  <svelte:element
    this={headingTag}
    class={twMerge(
      'leading-snug transition-all duration-300 group-hover:text-blue',
      scheme == 'dark' && ' text-white group-hover:text-blue-400',
      headingSize && headingClasses[headingSize]
    )}
  >
    {title}
  </svelte:element>
  {#if description}
    <p
      class={twMerge(
        'hidden items-center text-sm md:block md:pt-3',
        scheme == 'dark' && 'text-white'
      )}
    >
      {description}
    </p>
  {/if}
  <div
    class={twMerge(
      'mt-auto flex flex-wrap items-center gap-x-1.5 pt-2.5 !text-xxs leading-none text-gray-700 md:pt-4',
      scheme == 'dark' && ' text-white',
      metadataClass
    )}
  >
    <div class="flex items-center gap-x-2.5">
      {#if authorImage}
        <ProfileImage
          src={authorImage}
          alt={authorImgAltText}
          class={twMerge(hideAuthorImageMobile && 'hidden md:block')}
        />
      {/if}

      {authorName}
    </div>
    {#if revisionDate}
      <span>&bull;</span>
      <RevisionDate datetime={revisionDate} />
    {/if}
  </div>
</div>

<!--
@component
This will serve as a child component of both ArticleListItem and ArticleCard.
## Props
@prop title: string;
@prop authorName: string;
@prop category: string = undefined;
@prop description: string = undefined;
@prop revisionDate: string = undefined;
@prop authorImage: string = undefined;
@prop authorImageAlt: string = undefined;
@prop headingTag: string = 'h3';
@prop headingSize: string = 'md';
@prop scheme: ColorScheme = 'light';
@prop categoryClass: string = undefined;
@prop metadataClass: string = undefined;
@prop hideAuthorImageMobile: boolean = false;

-->
